import { differenceInCalendarDays, getDate, format } from 'date-fns';
import { policyTypes as PolicyType, isFarmBasedPolicyType } from '@ourbranch/policy-types';
import {
  alabamaLogicInstallmentFeeStates,
  fixedHomeInstallmentFeeStates,
  localPremiumTaxStates,
  statePremiumTaxStates,
  zeroPremiumTaxCities,
  shortRateFeeStates,
  getFinancialResponsibilityTier,
  directAffinityCodes,
  altCreditAlgorithmStatesDates,
  standaloneRentersStates as StandaloneRentersStates,
  condoStates as CondoStates,
  flatInstallmentFee,
  paymentType as PaymentType,
  paymentMethod as PaymentMethod
} from '@ourbranch/lookups';

import { getStringFromDate, getTodayStr, getDateFromString, getPolicyEndDate } from '@ourbranch/date-fns-helpers';

const installmentCountByTypeAndState = {
  A: {
    LA: 5,
    NH: 5
  },
  H: {
    LA: 11,
    NH: 11
  }
};

const nonBixNonThreeDollarInstallmentFeeStates = {
  IL: true,
  MO: true,
  TX: true
};

const getInventoryDiscount = ({
  policyType,
  state,
  inBundle = false,
  isBix
}: {
  policyType: string;
  state: string;
  inBundle: boolean;
  isBix: boolean;
}) => {
  if (policyType === PolicyType.Auto || policyType === PolicyType.ARBundle) {
    if (state === 'TX') {
      // auto and auto/renters bundle in TX
      return 0;
    }
    if (state === 'IL' || (state === 'MO' && !isBix)) {
      // auto and auto/renters bundle in IL & MO GSNIC
      return inBundle ? 15 : 20;
    }
  }
  if (state === 'IL') {
    // home, IL
    return 50;
  }
  // home and home/auto bundle, elsewhere
  return inBundle ? 55 : 65;
};

export const FeeType = {
  Policy: 'policy',
  DwellingPolicy: 'dwelling_policy',
  Imagery: 'imagery',
  Paper: 'paper',
  Installment: 'installment',
  Cancellation: 'cancellation',
  Reinstatement: 'reinstatement',
  AutoTheft: 'auto_theft',
  PoliceTraining: 'police_training',
  RuralFireDept: 'rural_fire_dept',
  FairPlan: 'fair_plan',
  AutoTheftPrevention: 'auto_theft_prevention',
  AutoCrimePrevention: 'auto_crime_prevention',
  MCCA: 'mcca',
  CreditCard: 'credit_card',
  Rewrite: 'rewrite',
  NSF: 'nsf',
  Late: 'late'
};

/**
 * Installment-fee tier for a payment type. Escrow and OneTime are annual-only (no installments),
 * so they have no tier (null) and generate no installment fee.
 */
function getInstallmentFeeTypeKey(paymentType: string): 'quarterly' | 'semiAnnual' | 'monthly' | null {
  if (paymentType === PaymentType.QuarterTerm) {
    return 'quarterly';
  }
  if (paymentType === PaymentType.HalfTerm) {
    return 'semiAnnual';
  }
  if (paymentType === PaymentType.Escrow || paymentType === PaymentType.OneTime) {
    return null;
  }
  return 'monthly';
}

// Resolves the per-LOB fees block that governs a policy type: fees.farm for farm-based types
// (F/Z/Y), fees.home for Home (H), fees.auto for Auto (A). Centralizes the lookup used by the
// brand-config-driven fee amounts (reinstatement / rewrite / nsf / late) and the fully-earned tail.
export function getLobFeesConfig(policyType: string, brandConfig: any) {
  const feesConfig = brandConfig?.config?.fees;
  if (!feesConfig) {
    return undefined;
  }
  if (isFarmBasedPolicyType(policyType)) {
    return feesConfig.farm;
  }
  if (policyType === PolicyType.Home) {
    return feesConfig.home;
  }
  if (policyType === PolicyType.Auto) {
    return feesConfig.auto;
  }
  return undefined;
}

/**
 * Whether fees are being generated for a new-business term: an offer (no term yet) or term 1, and
 * not a renewal context. Renewal processors pass `isRenewal` (auto) or the renewal's own term (farm);
 * an endorsement passes the policy record's term.
 */
function isNewBusinessTerm({ term, isRenewal }: { term?: number | string | null; isRenewal?: boolean }): boolean {
  return !isRenewal && (term === undefined || term === null || Number(term) === 1);
}

/**
 * The legacy "N Pay" count a brand bills a Monthly auto term at NEW BUSINESS (CFM: GO's
 * "Automated 5 Pay-NB", fees.auto.newBusinessMonthlyInstallmentCount, #36492), or undefined when the
 * brand has no such plan or the plan isn't Monthly. Rating-time only: the offer rater prices and
 * schedules the offer with it, and the bind stamps it on the policy as `newBusinessInstallmentCount`.
 * Billing and endorsements read the STAMP, never this config, so a policy bound on an earlier plan
 * keeps the schedule it was sold on; renewals bill the term length (GO's "6 Pay-Renewal").
 */
export function newBusinessAutoMonthlyPlanCount({
  paymentType,
  brandConfig
}: {
  paymentType?: string | null;
  brandConfig?: any;
}): number | undefined {
  if (paymentType !== PaymentType.Monthly) {
    return undefined;
  }
  const planCount = Number(brandConfig?.config?.fees?.auto?.newBusinessMonthlyInstallmentCount);
  return planCount > 0 ? planCount : undefined;
}

/**
 * Installments a NEW-BUSINESS 6-month auto term is sold on: the brand's new-business Monthly plan
 * when it has one (CFM bills 5, #36492), otherwise the term length for Monthly (6), 2 for the
 * 2-payment plan and 1 for everything else. Rating-time: the offer's bill amount, down payment and
 * card fee all count with this; once bound, the policy's `newBusinessInstallmentCount` stamp is the
 * source of truth.
 */
export function autoInstallmentCount({ paymentType, brandConfig }: { paymentType: string; brandConfig?: any }): number {
  return newBusinessAutoMonthlyPlanCount({ paymentType, brandConfig }) ?? autoPaymentCount(paymentType);
}

/**
 * Single source of truth for the fully-earned rule (issue #33114). A fully-earned fee is collected
 * in full on the down payment / first renewal installment and is non-refundable. The decision is
 * entirely brand-config driven and resolved from the per-LOB fees block that governs this policy
 * type (fees.farm for F/Z/Y, fees.home for H, fees.auto for A) — NOT gated on policy type — so any
 * LOB can opt in (Forreston farm, CFM auto) while brands without the config never earn fees fully.
 * Two config fields, both required: `fullyEarnedFeeTypes` (which fee types earn fully) and
 * `fullyEarnedFeesMinimumEffectiveDate` (the TERM effective date the switch turns on).
 *
 * Extracted from generateFee so the renewal-creator defensive normalization and the
 * migrated-policy backfill (#35355) apply the EXACT same decision rather than re-deriving it —
 * the #35355 bug was a FarmHome (Z) renewal missing the flag a FarmRental (Y) one carried.
 */
export function isFeeFullyEarned({
  feeType,
  policyType,
  effectiveDate,
  amount,
  brandConfig
}: {
  feeType: string;
  policyType: string;
  effectiveDate: string;
  amount: number;
  brandConfig: any;
}): boolean {
  const lobFeesConfig = getLobFeesConfig(policyType, brandConfig);
  const fullyEarnedFeeTypes: string[] = Array.isArray(lobFeesConfig?.fullyEarnedFeeTypes)
    ? lobFeesConfig.fullyEarnedFeeTypes.map((t: string) => String(t).toLowerCase())
    : [];
  const fullyEarnedMinEffectiveDate = lobFeesConfig?.fullyEarnedFeesMinimumEffectiveDate;
  const effectiveDateInt = Number(String(effectiveDate).replace(/-/g, ''));
  return (
    fullyEarnedFeeTypes.includes(String(feeType).toLowerCase()) &&
    amount > 0 &&
    !!fullyEarnedMinEffectiveDate &&
    effectiveDateInt >= Number(String(fullyEarnedMinEffectiveDate).replace(/-/g, ''))
  );
}

/**
 * Re-asserts the fully-earned flag across a fee array using the brand-config rule (issue #35355).
 * For each fee: sets `fullyEarned: true` when {@link isFeeFullyEarned} says it should be, and removes
 * a stale `fullyEarned` when it should not. Mutates in place and returns how many fees it changed.
 *
 * This is the defensive normalization the renewal creator runs after generating fees and before
 * persisting — so a fee that was built or rebuilt without the flag (the #35355 symptom, where a
 * FarmHome renewal shipped with an un-flagged Policy Fee that then spread across the schedule) is
 * corrected in lockstep with generateFee. Idempotent: safe to run on already-correct fees.
 */
export function applyFullyEarnedToFees(
  fees: Array<{ type?: string; amount?: number; fullyEarned?: boolean }>,
  { policyType, effectiveDate, brandConfig }: { policyType: string; effectiveDate: string; brandConfig: any }
): number {
  if (!Array.isArray(fees)) {
    return 0;
  }
  let changed = 0;
  for (const fee of fees) {
    const shouldBeFullyEarned = isFeeFullyEarned({
      feeType: String(fee.type ?? ''),
      policyType,
      effectiveDate,
      amount: Number(fee.amount ?? 0),
      brandConfig
    });
    if (shouldBeFullyEarned && fee.fullyEarned !== true) {
      fee.fullyEarned = true;
      changed += 1;
    } else if (!shouldBeFullyEarned && fee.fullyEarned === true) {
      delete fee.fullyEarned;
      changed += 1;
    }
  }
  return changed;
}

/**
 * Returns the MCCA assessment rates for a given effective date.
 * Rates are per-vehicle for a 6-month term.
 * @param effectiveDateInt - Effective date as an integer (e.g., 20260701)
 * @returns { mccaBaseFee, mccaOtherFee } - Base fee for UNLIMITED PIP; other fee for non-UNLIMITED/opt-out
 */
export function getMccaRates(effectiveDateInt: number): { mccaBaseFee: number; mccaOtherFee: number } {
  if (effectiveDateInt >= 20260701) {
    return { mccaBaseFee: 42, mccaOtherFee: 9.5 };
  }
  if (effectiveDateInt >= 20240701) {
    return { mccaBaseFee: 45, mccaOtherFee: 10 };
  }
  if (effectiveDateInt >= 20230701) {
    return { mccaBaseFee: 61, mccaOtherFee: 24 };
  }
  return { mccaBaseFee: 43, mccaOtherFee: 0 };
}

/**
 *
 * @param {string} feeType, cant be A for autoPolicy fees or H for HomePolicyFees
 * @param {object} feeOptions: { policyType, inBundle, state }
 */
export function generateFee(feeType: string, feeOptions: any) {
  let feeDescription;
  let feeAmount = 0;
  let deviceCost;

  const {
    policyType,
    state,
    inBundle,
    isBix,
    policyLimitPIPME,
    premium,
    effectiveDate,
    billingDayOfMonth,
    isMegaDownPay,
    cancelCode,
    term,
    brandConfig,
    dwellingCount,
    paymentMode,
    paymentType,
    paymentCount,
    isRenewal
  } = feeOptions;
  const effectiveDateInt = Number(String(effectiveDate).replace(/-/g, ''));
  let calculatedPaymentCount =
    policyType === PolicyType.Auto
      ? (<any>installmentCountByTypeAndState)[policyType]?.[state] || 6
      : (<any>installmentCountByTypeAndState)[policyType]?.[state] || 12;

  if (isMegaDownPay || (term === 1 && billingDayOfMonth === Number(effectiveDate?.split('-')[2]))) {
    calculatedPaymentCount -= 1;
  }

  // The auto 2-payment plan (CFM, #36200) is always exactly two installments — the Monthly-only
  // billing-day / mega-down-pay adjustments above don't apply to it.
  if (policyType === PolicyType.Auto && paymentType === PaymentType.HalfTerm) {
    calculatedPaymentCount = autoPaymentCount(paymentType);
  }

  // Use passed-in paymentCount if provided, otherwise use calculated value. A brand's fixed
  // new-business auto Monthly plan (CFM's 5 Pay, #36492) always arrives here as an explicit count —
  // from the brand config at new business (cd-auto-rater), from the policy's `newBusinessInstallmentCount`
  // stamp on an endorsement — so a policy bound before the plan existed keeps its own count.
  const finalPaymentCount = paymentCount !== undefined ? paymentCount : calculatedPaymentCount;

  // GO pay-plan parity (#36489): when the brand keeps the installment fee off the new-business down
  // payment (fees.<lob>.installmentFeeOnDownPayment: false), a new-business term charges it on
  // installments 2..N only — one fee fewer than the installment count — and the line is flagged so the
  // down payment leaves it out (getDownPayment). A renewal term charges it on every installment, the
  // first included (the renewal first installment is not a down payment). Brands without the setting
  // keep fee × N spread evenly over all N.
  // `installmentFeeOnDownPayment` in feeOptions overrides the brand config: an endorsement regenerating
  // a stored fee passes the rule that fee was built under, so a term bound before the brand switched
  // rules keeps fee × N (and its down payment) rather than changing price on a $0 endorsement.
  const installmentFeeOnDownPayment =
    feeOptions.installmentFeeOnDownPayment ?? getLobFeesConfig(policyType, brandConfig)?.installmentFeeOnDownPayment;
  const installmentFeeExcludedFromDownPayment =
    installmentFeeOnDownPayment === false && isNewBusinessTerm({ term, isRenewal });
  const installmentFeeCount = installmentFeeExcludedFromDownPayment
    ? Math.max(finalPaymentCount - 1, 0)
    : finalPaymentCount;

  const { mccaBaseFee, mccaOtherFee } = getMccaRates(effectiveDateInt);

  switch (feeType.toLowerCase()) {
    case 'policy':
      feeDescription = 'Policy';
      if (isFarmBasedPolicyType(policyType)) {
        // Check if brand applies policy fee (default to true if not specified)
        const applyFee = brandConfig?.config?.fees?.farm?.applyPolicyFee ?? true;
        if (applyFee) {
          // Check brand config first, fall back to $150
          feeAmount = brandConfig?.config?.fees?.farm?.policyFee ?? 150;
        } else {
          feeAmount = 0;
        }
      } else if (policyType === PolicyType.Home && brandConfig?.config?.fees?.home) {
        // Brand-config-driven home policy fee (Oakwood). Brands without
        // `fees.home` fall through to the legacy state-based logic below.
        const applyFee = brandConfig.config.fees.home.applyPolicyFee ?? true;
        feeAmount = applyFee ? (brandConfig.config.fees.home.policyFee ?? 5) : 0;
      } else if (policyType === PolicyType.Auto && brandConfig?.config?.fees?.auto) {
        // Brand-config-driven auto policy fee (CFM). Must come before the legacy
        // `MO && !isBix → 0` branch below, since CFM rates in MO on non-bix paper and
        // would otherwise fall through to $0. Brands without `fees.auto` keep the legacy
        // state-based logic. Rewrite ($25) is applied via the separate `rewrite` fee type.
        const autoFees = brandConfig.config.fees.auto;
        const applyFee = autoFees.applyPolicyFee ?? true;
        if (!applyFee) {
          feeAmount = 0;
        } else if (isRenewal && autoFees.renewalPolicyFee !== undefined) {
          // "Policy Fee At Renewal" (CFM auto $25 vs $35 new business, issue #34828). Only used
          // when a renewal context passes isRenewal; otherwise the new-business policyFee applies.
          feeAmount = autoFees.renewalPolicyFee;
        } else {
          // Default $0 (not $5): $5 is the home/legacy default — a brand that opts into fees.auto
          // without a policyFee should charge nothing rather than a phantom legacy amount.
          feeAmount = autoFees.policyFee ?? 0;
        }
      } else if ((state === 'MO' && !isBix) || (state === 'TX' && !isBix) || (state === 'AL' && policyType === 'H')) {
        feeAmount = 0;
      } else {
        feeAmount = 5;
      }
      break;
    case 'dwelling_policy':
      feeDescription = 'Dwelling Policy';
      if (isFarmBasedPolicyType(policyType) && dwellingCount > 1) {
        // Check if brand applies dwelling policy fee (default to true if not specified)
        const applyFee = brandConfig?.config?.fees?.farm?.applyDwellingPolicyFee ?? true;
        if (applyFee) {
          const feePerDwelling = brandConfig?.config?.fees?.farm?.dwellingPolicyFeePerDwelling ?? 0;
          feeAmount = feePerDwelling * (dwellingCount - 1);
        } else {
          feeAmount = 0;
        }
      } else {
        feeAmount = 0;
      }
      break;
    case 'paper':
      feeDescription = 'Paper Documentation';
      feeAmount = state === 'TX' && !isBix ? 25 : 0;
      break;
    case 'imagery': {
      feeDescription = 'Imagery';
      feeAmount =
        policyType === PolicyType.HABundle
          ? getInventoryDiscount({ policyType: 'H', state, inBundle: true, isBix }) +
            getInventoryDiscount({ policyType: 'A', state, inBundle: true, isBix })
          : getInventoryDiscount({ policyType, state, inBundle, isBix });
      break;
    }
    case 'installment':
      feeDescription = 'Installment';
      if (isFarmBasedPolicyType(policyType)) {
        // Check if brand applies installment fees (default to true if not specified)
        const applyFee = brandConfig?.config?.fees?.farm?.applyInstallmentFees ?? true;
        if (applyFee) {
          // Check brand config for installment fees
          const brandFees = brandConfig?.config?.fees?.farm?.installmentFees;
          let perInstallmentFee = 2; // default

          if (brandFees) {
            // Determine payment mode and type to lookup correct fee
            const modeKey = paymentMode === 'D' ? 'directBill' : 'automated';

            // Escrow and OneTime payment types are annual-only with no installments
            // If these are passed in, use a fallback (though they shouldn't generate installment fees)
            const typeKey = getInstallmentFeeTypeKey(paymentType);

            perInstallmentFee = typeKey ? (brandFees[modeKey]?.[typeKey] ?? 2) : 0;
          }

          feeAmount = perInstallmentFee * installmentFeeCount;
        } else {
          feeAmount = 0;
        }
      } else if (policyType === PolicyType.Home && brandConfig?.config?.fees?.home) {
        // Brand-config-driven home installment fee (Oakwood). Brands without
        // `fees.home` fall through to the legacy state-based logic below.
        const applyFee = brandConfig.config.fees.home.applyInstallmentFees ?? true;
        if (applyFee) {
          const brandFees = brandConfig.config.fees.home.installmentFees;
          let perInstallmentFee = 2;
          if (brandFees) {
            const modeKey = paymentMode === 'D' ? 'directBill' : 'automated';
            const typeKey = getInstallmentFeeTypeKey(paymentType);
            // `PolicyTypeFeesConfig.installmentFees.directBill` intentionally has no
            // `monthly` tier today (Oakwood only offers monthly via automated billing).
            // Monthly + Direct Billing falls back to $2. If a future brand adds Monthly
            // + Direct Billing at a different rate, extend `directBill` with `monthly`
            // and set the value in that brand's config rather than relying on this
            // fallback.
            perInstallmentFee = typeKey ? (brandFees[modeKey]?.[typeKey] ?? 2) : 0;
          }
          feeAmount = perInstallmentFee * installmentFeeCount;
        } else {
          feeAmount = 0;
        }
      } else if (policyType === PolicyType.Auto && brandConfig?.config?.fees?.auto?.installmentFees) {
        // Brand-config-driven auto installment fee (CFM auto: $7 direct-bill / $2 automated,
        // issue #34828). Must come before the legacy MO/non-bix branch below, since CFM rates in
        // MO and would otherwise fall through to the $4/installment legacy amount. Brands without
        // `fees.auto.installmentFees` keep the legacy state-based logic. Auto supports a `monthly`
        // direct-bill tier (unlike farm/home, which bill monthly only via automated).
        const applyFee = brandConfig.config.fees.auto.applyInstallmentFees ?? true;
        if (applyFee) {
          const brandFees = brandConfig.config.fees.auto.installmentFees;
          const modeKey = paymentMode === 'D' ? 'directBill' : 'automated';
          const typeKey = getInstallmentFeeTypeKey(paymentType);
          const perInstallmentFee = typeKey ? (brandFees[modeKey]?.[typeKey] ?? 2) : 0;
          feeAmount = perInstallmentFee * installmentFeeCount;
        } else {
          feeAmount = 0;
        }
      } else if (!isBix && (<any>nonBixNonThreeDollarInstallmentFeeStates)[state]) {
        feeAmount = feeOptions.paymentMethod === 'E' ? 1 * finalPaymentCount : 4 * finalPaymentCount;
      } else {
        feeAmount = flatInstallmentFee * finalPaymentCount; // flat $3/installment for BIX and new GSNIC
      }
      break;
    case 'cancellation':
      feeDescription = 'Cancellation';
      if (state === 'SC') {
        feeAmount = 10;
      } else if (state === 'AK') {
        // For Alaska, the cancellation fee is 7.5% of the unearned premium IF the cancellation reason is included
        // in the list below. If the cancellation reason is not included in the list below, no cancellation fee should apply
        if (['CNDB', 'CNDC', 'CNNO', 'CNDA', 'CNPR'].includes(cancelCode)) {
          // Both dates parsed timezone-free (getPolicyEndDate + getDateFromString anchor to
          // local midnight) so the day count is stable off the UTC Lambda runtime (issue #34738).
          const effectiveDateJS = getDateFromString(effectiveDate);
          const policyEndDate = getDateFromString(getPolicyEndDate(effectiveDate, policyType));
          const policyDaysRemaining = differenceInCalendarDays(policyEndDate, new Date());
          const perDayPremium = getDailyRate(premium, policyEndDate, effectiveDateJS);
          feeAmount = roundTwoDecimals(perDayPremium * policyDaysRemaining * 0.075);
        }
      } else if (state !== 'ND') {
        feeAmount = 35;
      }
      break;
    case 'short_rate':
      feeDescription = 'Short Rate VA Cancellation Amount';
      feeAmount = (premium || 1) * 0.1; // need to know premium here to figure out unearned premium
      break;
    case 'reinstatement':
      feeDescription = 'Reinstatement';
      if ((state === 'MI' && policyType === 'A') || (state === 'SD' && policyType === 'H') || state === 'ND') {
        // State-mandated $0 — takes precedence over any brand-config amount.
        feeAmount = 0;
      } else {
        // Brand-config-driven reinstatement fee (CFM farm $50 / auto $25, issue #34828), resolved
        // from the per-LOB fees block. Brands without it keep the legacy flat $5.
        feeAmount = getLobFeesConfig(policyType, brandConfig)?.reinstatementFee ?? 5;
      }

      break;
    case 'rewrite':
      // Rewrite policy fee (CFM auto $25, issue #34828). Applied as a manual billing-section fee;
      // amount is brand-config driven per LOB. No configured amount → $0.
      feeDescription = 'Rewrite';
      feeAmount = getLobFeesConfig(policyType, brandConfig)?.rewritePolicyFee ?? 0;
      break;
    case 'nsf':
      // NSF / returned-payment fee (CFM farm $35 / auto $25, issue #34828). Brand-config driven per
      // LOB; applied as a manual billing-section fee. No configured amount → $0.
      feeDescription = 'NSF';
      feeAmount = getLobFeesConfig(policyType, brandConfig)?.nsfFee ?? 0;
      break;
    case 'late':
      // Late payment fee (CFM auto $10; farm not assessed, issue #34828). Brand-config driven per
      // LOB; applied as a manual billing-section fee. No configured amount → $0.
      feeDescription = 'Late Payment';
      feeAmount = getLobFeesConfig(policyType, brandConfig)?.latePaymentFee ?? 0;
      break;
    case 'police_training':
      feeDescription = 'State Police Training and Academy Fund Assessment';
      feeAmount =
        2 *
        (Array.isArray(feeOptions.cars)
          ? feeOptions.cars.filter((c: any) => c.deductibleComprehensive !== 'NONE').length
          : 1);
      break;
    case 'auto_theft':
      feeDescription = 'Auto Theft Authority';
      feeAmount = 0.5 * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1);
      break;
    case 'mcca':
      feeDescription = 'MCCA Full Recoupment Fee';
      feeAmount =
        policyLimitPIPME && policyLimitPIPME === 'UNLIMITED'
          ? mccaBaseFee * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1)
          : mccaOtherFee * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1);
      break;
    case 'rural_fire_dept':
      feeDescription = 'Rural Volunteer Fire Department Assistance Program Recoupment Fee';
      feeAmount = 0;
      break;
    case 'fair_plan':
      feeDescription = 'FAIR Plan Surcharge';
      feeAmount = 0;
      break;
    case 'home_hazard':
      feeDescription = 'Home Hazard Mitigation Fee';
      feeAmount = effectiveDateInt >= 20220301 ? 2 : 0;
      break;
    case 'auto_theft_prevention':
      feeDescription = 'Auto Theft Prevention';
      deviceCost = state === 'TX' && policyType !== 'H' ? 2 : 0.5;
      feeAmount =
        deviceCost *
        (Array.isArray(feeOptions.cars)
          ? feeOptions.cars.filter((c: any) => c.deductibleComprehensive !== 'NONE').length
          : 1);
      break;
    case 'auto_crime_prevention':
      feeDescription = 'Motor Vehicle Crime Prevention';
      feeAmount = 2 * (Array.isArray(feeOptions.cars) ? feeOptions.cars.length : 1);
      break;

    case 'credit_card': {
      const paymentDate = getStringFromDate(feeOptions.paymentDate || new Date(), 'MM/dd/yyyy');
      feeDescription = `Credit Card Fee for ${paymentDate} Payment`;
      if (appliesCreditCardFee({ brandConfig, policyType })) {
        feeAmount = parseFloat((0.25 + (premium || 0) * 0.0285).toFixed(2));
      } else {
        feeAmount = 0;
      }
      break;
    }

    default:
      break;
  }

  // Fully-earned fees (issue #33114). A fully-earned fee is collected in full on the down payment /
  // first renewal installment and is non-refundable. Fully brand-config driven and NOT gated on
  // policy type — it is resolved from the per-LOB fees block that governs this policy type
  // (fees.farm for F/Z/Y, fees.home for H, fees.auto for A), the same way the fee AMOUNT is resolved
  // above. So any LOB can opt in (e.g. Forreston farm, or CFM Auto via fees.auto) while brands
  // without the config (BIX/Branch) never earn fees fully. Two fields on that block, both required:
  //   - `fullyEarnedFeeTypes`: the fee types the brand earns fully (e.g. ['policy', 'dwelling_policy']).
  //     Nothing is hardcoded — a brand can list any fee type (or none).
  //   - `fullyEarnedFeesMinimumEffectiveDate`: the term effective date the switch turns on, checked
  //     against the policy TERM effective date so in-force pre-cutover policies (and their mid-term
  //     endorsements, which retain the original term effective date) keep Paid Premium functioning
  //     until they renew on/after the cutover.
  const fullyEarned = isFeeFullyEarned({ feeType, policyType, effectiveDate, amount: feeAmount, brandConfig });

  const excludedFromDownPayment = feeType === 'installment' && installmentFeeExcludedFromDownPayment && feeAmount > 0;

  return {
    description: feeDescription,
    amount: feeAmount,
    ...(fullyEarned ? { fullyEarned: true } : {}),
    ...(excludedFromDownPayment ? { excludedFromDownPayment: true } : {})
  };
} // generateFee

function roundTwoDecimals(num: number) {
  return Math.round(num * 1e2) / 1e2;
}

// Rounds an installment amount UP to the next cent so an even-split down payment exactly equals
// the recurring installment rows (@ourbranch/billing imports this as its ceilCurrency — single
// definition on purpose, the two must never drift), pushing the rounding shortfall onto the FINAL
// payment. The inner round-to-micro-cents guards float artifacts
// (e.g. 100 * 100 === 10000.000000000002) from ceiling an exact amount up a phantom cent.
export function ceilTwoDecimals(num: number) {
  return Math.ceil(Math.round(num * 1e6) / 1e4) / 1e2;
}

function getPremiumBreakdownForFees({ coverages, premium, policyType, premiumBreakdown }: any) {
  let homePremium = 0;
  let sppPremium = 0;
  let umbrellaPremium = 0;
  let autoPremium = 0;
  let rentersPremium = 0;
  let condoPremium = 0;
  // do we need this logic for coverages? we are already calculating the spp on bind request to accounts
  if (coverages) {
    if (policyType === 'A') {
      rentersPremium = coverages
        .filter((c: any) => c.type === 'renters_total')
        .reduce((acc: any, c: any) => {
          return acc + c.amount;
        }, 0);
      autoPremium = premium - rentersPremium;
    }
    // not sure if we want to include condo here
    if (policyType === 'H') {
      sppPremium = (coverages.find((x: any) => x.type.toLowerCase() === 'spp_total') || {}).amount || 0;
      umbrellaPremium =
        (coverages.find((x: any) => x.type.toLowerCase() === 'personal_umbrella_liability_total') || {}).amount || 0;
      homePremium = premium - (sppPremium + umbrellaPremium);
    }
  } else if (premiumBreakdown) {
    condoPremium = premiumBreakdown.condo || 0;
    homePremium = premiumBreakdown.home || 0;
    sppPremium = premiumBreakdown.spp || 0;
    umbrellaPremium = premiumBreakdown.umbrella || 0;
    autoPremium = premiumBreakdown.car || 0;
    rentersPremium = premiumBreakdown.renters || 0;
  } else {
    throw new Error('Did not receive coverages or premium breakdown as part of local premium tax calculations.');
  }
  return {
    homePremium,
    sppPremium,
    umbrellaPremium,
    autoPremium,
    rentersPremium,
    condoPremium
  };
}

function getLocalTaxRates({ localPremiumTax }: any): any {
  const retObj = {
    minimum: 0
  };
  const lookupCodes = [
    { name: 'home', code: 'KY01' },
    { name: 'spp', code: 'KY04' },
    { name: 'umbrella', code: 'KY02' },
    { name: 'auto', code: 'KY03' },
    { name: 'renters', code: 'KY01' },
    { name: 'condo', code: 'KY01' }
  ];

  const { minimumTax } = localPremiumTax;

  if (minimumTax && minimumTax !== 'N/A') {
    const minimumCandidate = Number(minimumTax.replace('$', ''));
    if (!Number.isNaN(minimumCandidate)) {
      retObj.minimum = minimumCandidate;
    }
  }

  for (const lookup of lookupCodes) {
    const { name, code } = lookup;
    const record = localPremiumTax?.lineOfBusinessDetails?.find((lob: { code: string }) => lob?.code === code);
    if (!record) {
      console.log(
        `could not find local premium tax (assuming not a taxable area) for ${name} with code ${code} in ${JSON.stringify(
          localPremiumTax,
          null,
          2
        )}`
      );
      (<any>retObj)[`${name}Percentage`] = 0;
    } else {
      const premiumTaxPercentageCandidate = record?.taxValue === 'NONE' ? 0 : parseInt(record?.taxValue, 10);
      if (Number.isNaN(premiumTaxPercentageCandidate)) {
        console.log(`premium tax candidate is NaN: ${record?.taxValue} / ${parseInt(record?.taxValue, 10)}`);
        throw new Error('Premium Tax is not a number.');
      }
      (<any>retObj)[`${name}Percentage`] = premiumTaxPercentageCandidate / 100;
    }
  } // for each thing we need a lookup
  return retObj;
}

function calculatePremiumTax({ premium, premiumPart, nonPremiumExtraTotal, percentage, minimum }: any) {
  // if premiumPart is 0, then no tax owed:
  if (premiumPart === 0) {
    return 0;
  }

  // spread nonPremiumExtraTotal pro-rata based upon the premium part of the total premium
  // and respect the minimum
  const calcValue = (premiumPart + (nonPremiumExtraTotal * premiumPart) / premium) * percentage;
  if (Number.isNaN(calcValue)) {
    console.log('calcValue was zero, so returning premium tax of 0');
    return 0;
  }
  console.log(
    `calculate premium tax: ${minimum}, ${calcValue}; yielding: ${minimum > calcValue ? minimum : calcValue}`
  );
  return minimum > calcValue ? minimum : calcValue;
}

/**
 *
 * @param {string} state, state in uppercase
 * @param {string} policyType, H or A
 * @param {object} fees: { amount, appliedDate, description, type }
 * @param {float} premium
 * @param {float} downPayment
 * @param {object} quote: quote object from an offer
 */
export function adjustFees({
  fees,
  state,
  policyType,
  premium,
  surplusContribution,
  localPremiumTax,
  coverages,
  premiumBreakdown,
  quote,
  optionType
}: any) {
  console.log(`adjustFees called with state ${state}`);
  // At this point, this function only implements the Alabama fee logic,
  // premium taxes, and short rating in VA cancels
  if ((<any>shortRateFeeStates)[state]) {
    const shortRateFee = fees.find((f: any) => f.type === 'short_rate');
    if (shortRateFee) {
      // we have a short rate fee stubbed out with the premium on the full policy
      // now premium is the earned amount, so subtracting the two will give us the unearned amount
      // and the short rate fee is 10% of that
      shortRateFee.amount = roundTwoDecimals((shortRateFee.amount - (premium || 0)) * 0.1);
    }
  } // short rating

  if ((<any>localPremiumTaxStates)[state]) {
    // first, set up the local premium tax fee:
    if (!fees.find((f: any) => f.type === 'local_premium_tax')) {
      fees.push({
        type: 'local_premium_tax',
        description: `${state} Municipal Tax`,
        appliedDate: getTodayStr(state),
        amount: 0
      });
    }

    const taxFee = fees.find((f: any) => f.type === 'local_premium_tax');
    // second, update it based upon the various variables:

    // non-tax fee total
    const feeTotal = fees
      .filter((fee: any) => !['local_premium_tax', 'state_premium_tax'].includes(fee?.type))
      .reduce((acc: any, curr: any) => acc + curr.amount, 0);
    const nonPremiumExtraTotal = (surplusContribution || 0) + (feeTotal || 0);

    // now we build up how much of each type of premium we have here:
    const premiumBreakdownResult = getPremiumBreakdownForFees({
      coverages,
      premium,
      policyType,
      premiumBreakdown
    });
    const { homePremium, sppPremium, umbrellaPremium, autoPremium, rentersPremium, condoPremium } =
      premiumBreakdownResult;
    console.log(
      `premium breakdown for fees: ${JSON.stringify(premiumBreakdown, null, 2)}; coverages: ${JSON.stringify(
        coverages,
        null,
        2
      )}`
    );

    // pull out address:
    const address =
      quote.submittedAddress ||
      quote.correctedAddress ||
      quote.home?.homeLocation ||
      quote.condo?.condoLocation ||
      quote.rentersCoverage?.rentersLocation ||
      quote.cars?.[0]?.garageLocation;
    const zeroPremiumTaxCitiesInState = zeroPremiumTaxCities[state as keyof typeof zeroPremiumTaxCities];
    const zeroPremiumTaxHere = zeroPremiumTaxCitiesInState
      ? !!zeroPremiumTaxCitiesInState[String(address.city).toUpperCase() as keyof typeof zeroPremiumTaxCitiesInState]
      : false;

    // local premium tax:
    if (!localPremiumTax && !zeroPremiumTaxHere) {
      throw new Error(`Could not find localPremiumTax node, and ${address.city} is not in zeroPremiumTaxCities.`);
    }

    if (localPremiumTax) {
      const localTax = getLocalTaxRates({ localPremiumTax });
      console.log(`localTax: ${JSON.stringify(localTax, null, 2)}`);
      const {
        homePercentage,
        sppPercentage,
        umbrellaPercentage,
        autoPercentage,
        rentersPercentage,
        minimum,
        condoPercentage
      } = localTax;

      const homeTax = calculatePremiumTax({
        premium,
        premiumPart: homePremium,
        nonPremiumExtraTotal,
        percentage: homePercentage,
        minimum
      });

      const condoTax = calculatePremiumTax({
        premium,
        premiumPart: condoPremium,
        nonPremiumExtraTotal,
        percentage: condoPercentage,
        minimum
      });

      const sppTax = calculatePremiumTax({
        premium,
        premiumPart: sppPremium,
        nonPremiumExtraTotal,
        percentage: sppPercentage,
        minimum
      });

      const umbrellaTax = calculatePremiumTax({
        premium,
        premiumPart: umbrellaPremium,
        nonPremiumExtraTotal,
        percentage: umbrellaPercentage,
        minimum
      });

      const autoTax = calculatePremiumTax({
        premium,
        premiumPart: autoPremium,
        nonPremiumExtraTotal,
        percentage: autoPercentage,
        minimum
      });

      const rentersTax = calculatePremiumTax({
        premium,
        premiumPart: rentersPremium,
        nonPremiumExtraTotal,
        percentage: rentersPercentage,
        minimum
      });

      const sumOfTaxes = homeTax + sppTax + umbrellaTax + autoTax + rentersTax + condoTax;
      console.log(`sum Of Taxes: ${sumOfTaxes}`);

      if (policyType === 'H') {
        taxFee.homeTax = homeTax;
        taxFee.sppTax = sppTax;
        taxFee.umbrellaTax = umbrellaTax;
      }
      if (policyType === 'A') {
        taxFee.autoTax = autoTax;
        taxFee.rentersTax = rentersTax;
      }
      if (policyType === 'R') {
        taxFee.sppTax = sppTax;
        taxFee.rentersTax = rentersTax;
      }
      if (policyType === 'C') {
        taxFee.condoTax = condoTax;
        taxFee.sppTax = sppTax;
        taxFee.umbrellaTax = umbrellaTax;
      }

      taxFee.amount = roundTwoDecimals(sumOfTaxes);
      taxFee.description = `${state} Municipal Tax${
        localPremiumTax?.territoryName ? ` - ${localPremiumTax.territoryName}` : ''
      }`;
    } else {
      // no local premium tax, and in a zero premium tax city, so we zero it out.
      taxFee.amount = 0;
      taxFee.description = `${address.city} has no local premium tax`;
    }
  } // local premium tax

  if ((<any>statePremiumTaxStates)[state]) {
    // first, set up the local premium tax fee:
    if (!fees.find((f: any) => f.type === 'state_premium_tax')) {
      fees.push({
        type: 'state_premium_tax',
        description: `${state} Insurance Premium Surcharge`,
        appliedDate: getTodayStr(state),
        amount: 0
      });
    }
    const taxFee = fees.find((f: any) => f.type === 'state_premium_tax');
    // second, update it based upon the various variables:

    // non-tax fee total
    const feeTotal = fees
      .filter((fee: any) => !['local_premium_tax', 'state_premium_tax'].includes(fee?.type))
      .reduce((acc: any, curr: any) => acc + curr.amount, 0);
    const underlyingTotal = premium + (surplusContribution || 0) + (feeTotal || 0);

    // state premium tax:
    const premiumTaxPercentage = 0.018;
    taxFee.amount = roundTwoDecimals(underlyingTotal * premiumTaxPercentage);
  } // state premium tax

  // if at some future point this function needs to do more, please refactor!
  if (!(<any>alabamaLogicInstallmentFeeStates)[state] || !Array.isArray(fees)) {
    return;
  }

  const installmentFee = fees.find((f) => f.type === 'installment');

  if (!installmentFee) {
    return;
  }

  const policyTypeMap = {
    [PolicyType.Auto]: 'auto',
    [PolicyType.Home]: 'home',
    [PolicyType.Renters]: 'renters',
    [PolicyType.Condo]: 'condo',
    // All farm-based types share the 'farm' global config namespace (farmEffectiveDate, farmPaymentMethod, ...)
    [PolicyType.Farm]: 'farm',
    [PolicyType.FarmHome]: 'farm',
    [PolicyType.FarmRental]: 'farm'
  };

  const feeTotal = fees.reduce((total, fee) => total + fee.amount, 0);

  const newDownPayment = getDownPayment({
    affinity: quote.global?.affinity,
    premium,
    policyType,
    optionType,
    state,
    quote,
    billingDay: quote.global?.[`${policyTypeMap[policyType]}BillingDayOfMonth`],
    paymentMethod:
      quote.global?.[`${policyType === PolicyType.Home ? 'homeowners' : policyTypeMap[policyType]}PaymentMethod`],
    total: premium + feeTotal + surplusContribution,
    effectiveDate: quote.global?.[`${policyTypeMap[policyType]}EffectiveDate`],
    fees,
    surplusContribution
  });

  const rate = policyType === 'A' ? 0.045 : 0.09;
  const paymentCount = policyType === 'A' ? 5 : 11; // don't count the downpayment
  const feeMin = 3;
  const altFeeCalc = roundTwoDecimals(
    Math.floor((((premium || 0) - (newDownPayment || 0)) * rate + 15) / paymentCount)
  );

  console.log(
    `fee adjustment from ${installmentFee.amount} to greater of ${feeMin} or ${altFeeCalc} (${premium}, ${newDownPayment}, ${rate}, ${paymentCount})`
  );

  installmentFee.amount = (altFeeCalc < feeMin ? altFeeCalc : feeMin) * (paymentCount + 1);
}

/**
 * Helper function to extract the first number from a BI limit string
 * Handles formats like "25/50/50" or "500 CSL"
 */
function extractFirstBILimitNumber(limitString: string | null | undefined): number {
  if (!limitString) {
    return 0;
  }

  let returnNumber = 0;

  if (!Number.isNaN(Number(limitString))) {
    returnNumber = Number(limitString);
  } else {
    // Extract first number before a slash or space
    const match = String(limitString).match(/^(\d+)(?:\/|\s|$)/);
    returnNumber = match ? Number(match[1]) : 0;
  }

  // we expect these numbers to be more than 1000; if they're less, then
  // it means they need to be multiplied by 1000 (e.g., we got 50 instead of 50000)
  if (returnNumber < 1000) {
    returnNumber = returnNumber * 1000;
  }

  return returnNumber;
}

export function getMinimumDownPayment({
  premium,
  policyType,
  state,
  quote,
  optionType,
  surplusContribution,
  upfrontFeeTotal
}: any) {
  const isAgency = quote.isAgentSold;
  const isPartner =
    !!quote.global.affinity && !quote.isAgentSold && !directAffinityCodes.includes(quote.global.affinity);

  const drivers = quote?.drivers?.length ? quote.drivers : quote.people;
  const primary = drivers.find((driver: any) => driver.isPrimary);
  const coApplicant = drivers.find((driver: any) => driver.isCoApplicant);
  const primaryInsuranceScore = primary?.insuranceScore?.autoTotal || primary?.insuranceScore?.total;
  const coAppInsuranceScore = coApplicant?.insuranceScore?.autoTotal || coApplicant?.insuranceScore?.total;
  const rateControlDateAsInt = Number(
    format(new Date(quote.global.rateControlDate || quote.global.autoEffectiveDate), 'yyyyMMdd')
  );
  const frTier = getFinancialResponsibilityTier(
    primaryInsuranceScore,
    coAppInsuranceScore,
    (<any>altCreditAlgorithmStatesDates)[state!] &&
      rateControlDateAsInt >= (<any>altCreditAlgorithmStatesDates)[state!],
    state
  ) as any;
  const clean = !drivers.some((d: any) => d.nonUDRIncidentCount > 0 || d.hasUDR);
  const isBundle = ['HA', 'CA'].includes(optionType);

  const isDirect = !isAgency && !isPartner;
  const policyMonths = policyType === PolicyType.Auto ? 6 : 12;
  let minimum = 0;
  let divisor = policyMonths;

  // total is premium + fees + surplus contribution
  const amountToUse = premium + upfrontFeeTotal + (surplusContribution || 0);

  if (policyType === PolicyType.Auto) {
    const priorBILimit = extractFirstBILimitNumber(quote.global.priorIndividualBILimit);
    const currentBILimit = extractFirstBILimitNumber(quote.autoCoverage.policyLimitBIPD);

    // Use the minimum of the two values
    const biFirstNum = Math.min(priorBILimit, currentBILimit);
    console.log(
      `[get-minimum-down-payment] Running rules in ${state} with isDirect of ${isDirect} and isAgency of ${isAgency} and isPartner of ${isPartner} and policyMonths of ${policyMonths} and premium of ${premium} and frTier of ${frTier} and clean of ${clean} and biFirstNum of ${biFirstNum} (min of prior: ${priorBILimit}, current: ${currentBILimit})`
    );

    const useInsuranceScore = !['MD', 'NM'].includes(state);
    // exception cases to the default:
    if (!useInsuranceScore && Number(biFirstNum) < 50000) {
      divisor = isBundle ? 5 : 4;
    } else if (useInsuranceScore && ['P-1', 'Q-1', 'R-1'].includes(frTier)) {
      divisor = isBundle ? 5 : 4;
    }
  } // special downpay rules for auto

  minimum = amountToUse / divisor;

  console.log(
    `[get-minimum-down-payment] minimum is ${minimum} (using divisor of ${divisor}) with upfrontFeeTotal of ${upfrontFeeTotal} and ${surplusContribution}`
  );

  // ceil (not round): when the divisor equals the term length this IS the recurring installment
  // amount, and it must match the ceiled schedule rows exactly so schedule row 0 = quoted down payment
  return ceilTwoDecimals(minimum || 0);
}

export function getDailyRate(total: number, endDate: Date, effectiveDate: Date) {
  const days = differenceInCalendarDays(endDate, effectiveDate) + 1;
  console.log('Get daily rate', total, endDate, effectiveDate, days);
  return total / days;
}

export function getDownPayment({
  affinity,
  premium,
  policyType,
  state,
  quote,
  billingDay,
  total,
  effectiveDate: effectiveDateIn,
  surplusContribution,
  fees,
  isMegaDownPay,
  optionType,
  paymentType,
  brandConfig
}: any) {
  let downPayment;
  // A down payment is always new business. CFM auto Monthly is GO's fixed 5 Pay plan (#36492): equal
  // installments, so it takes the even-split branch below instead of the risk-based minimum.
  const fixedAutoMonthlyCount =
    policyType === PolicyType.Auto ? newBusinessAutoMonthlyPlanCount({ paymentType, brandConfig }) : undefined;
  const isTwelveMonthPolicyTerm =
    policyType === PolicyType.Home ||
    isFarmBasedPolicyType(policyType) ||
    (policyType === PolicyType.Renters && (<any>StandaloneRentersStates)[state]) ||
    (policyType === PolicyType.Condo && (<any>CondoStates)[state]);

  // Calculate installment count based on payment type and policy type.
  // OneTime / Escrow are single-charge payment types — the down payment IS the full
  // total. Without this branch they fall into the Monthly default and get divided
  // by the policy term length, which silently undercharges a OneTime CC bind by
  // ~12× (issue: PR 30723 review). Mirrors farmPaymentCount() below.
  let installmentCount;
  if (paymentType === PaymentType.QuarterTerm) {
    installmentCount = 4;
  } else if (paymentType === PaymentType.HalfTerm) {
    installmentCount = 2;
  } else if (paymentType === PaymentType.OneTime || paymentType === PaymentType.Escrow) {
    installmentCount = 1;
  } else {
    // Monthly payments - use policy term length, or the brand's fixed new-business plan
    installmentCount = fixedAutoMonthlyCount ?? (isTwelveMonthPolicyTerm ? 12 : 6);
  }

  // fixed installment fee states just lock fixed across all installments; the auto 2-payment plan
  // (CFM, #36200) and a brand's fixed auto Monthly plan (#36492) are even splits too
  if (
    isFarmBasedPolicyType(policyType) ||
    ((<any>fixedHomeInstallmentFeeStates)[state] && policyType === PolicyType.Home) ||
    (policyType === PolicyType.Auto && paymentType === PaymentType.HalfTerm) ||
    fixedAutoMonthlyCount
  ) {
    // Fees that must be collected in full on the first invoice get front-loaded onto the down
    // payment instead of being spread across all installments; subsequent installments use the
    // base recurring amount. Two flags qualify:
    //   - firstInstallmentOnly: e.g. CC fee for direct billing where only installment #1 is paid via CC at bind.
    //   - fullyEarned: Policy Fee + Per Dwelling Fee for Forreston farm terms effective 8/1/2026+ (issue #33114).
    const frontLoadedFeeTotal = Array.isArray(fees)
      ? fees
          .filter((f: any) => f?.firstInstallmentOnly || f?.fullyEarned)
          .reduce((acc: number, f: any) => acc + (f.amount || 0), 0)
      : 0;
    // The opposite case (#36489): a new-business installment fee flagged `excludedFromDownPayment`
    // (generateFee, fee × (N − 1)) is charged on installments 2..N only, so it comes out of the base the
    // down payment is split from; the recurring rows (billing's residual split) then each carry one fee.
    const excludedFromDownPaymentFeeTotal = Array.isArray(fees)
      ? fees.filter((f: any) => f?.excludedFromDownPayment).reduce((acc: number, f: any) => acc + (f.amount || 0), 0)
      : 0;
    if (frontLoadedFeeTotal > 0 || excludedFromDownPaymentFeeTotal > 0) {
      // ceil the base so it matches the recurring installment rows; the front-loaded fees sit on top
      const baseInstallment = ceilTwoDecimals(
        (total - frontLoadedFeeTotal - excludedFromDownPaymentFeeTotal) / installmentCount
      );
      downPayment = baseInstallment + frontLoadedFeeTotal;
    } else {
      downPayment = ceilTwoDecimals(total / installmentCount);
    }
  } else {
    const upfrontTotalFee = fees.reduce((acc: any, f: any) => acc + f.amount, 0);
    // otherwise, logic is more complex
    downPayment = getMinimumDownPayment({
      affinity,
      premium,
      policyType,
      state,
      quote,
      optionType,
      surplusContribution,
      upfrontFeeTotal: upfrontTotalFee
    });

    const effectiveDate = new Date(`${effectiveDateIn}T00:00:00`);

    console.log('[get down payment]  fee-utils');
    console.log(`downPayment is ${downPayment}`);
    console.log('----------');

    // make sure down payment is not more than 80% of the total premium
    if (downPayment > total * 0.8) {
      console.log(
        `[fee-utils] -> [getDownPayment] -> down payment of ${roundTwoDecimals(
          downPayment
        )} is > 80% of the total price of the policy, adjusting to ${roundTwoDecimals(total * 0.8)}`
      );
      downPayment = total * 0.8;
    }

    // The merge logic refers to this issue:
    // https://github.com/gobranch/branch/issues/3886
    if (getDate(effectiveDate) === billingDay || isMegaDownPay) {
      downPayment += (total - downPayment) / (isTwelveMonthPolicyTerm ? 11 : 5);
    }
  } // more complex installment-fee logic
  console.log(`downPayment with megadownpayment is ${downPayment}`);
  return roundTwoDecimals(downPayment);
}

/**
 * Returns the number of scheduled installments for a farm policy, given its payment type.
 * Monthly → 12, QuarterTerm → 4, HalfTerm → 2, anything else (annual/escrow/onetime) → 1.
 */
export function farmPaymentCount(paymentType: string): number {
  switch (paymentType) {
    case PaymentType.Monthly:
      return 12;
    case PaymentType.QuarterTerm:
      return 4;
    case PaymentType.HalfTerm:
      return 2;
    default:
      return 1;
  }
}

/**
 * Returns the number of scheduled installments for a 6-month auto term, given its payment type.
 * Monthly → 6, HalfTerm (the CFM 2-payment plan, #36200) → 2, anything else → 1.
 */
export function autoPaymentCount(paymentType: string): number {
  switch (paymentType) {
    case PaymentType.Monthly:
      return 6;
    case PaymentType.HalfTerm:
      return 2;
    default:
      return 1;
  }
}

/**
 * Whether the brand charges the credit-card processing fee on this policy type. Gated per LOB on the
 * fee block that governs it — fees.farm for F/Z/Y, fees.auto for A (CFM charges it on every line,
 * #36200; Forreston on farm only). Brands without the toggle (Branch/BIX) never charge it.
 */
export function appliesCreditCardFee({ brandConfig, policyType }: { brandConfig?: any; policyType: string }): boolean {
  const fees = brandConfig?.config?.fees;
  if (isFarmBasedPolicyType(policyType)) {
    return !!fees?.farm?.applyCreditCardFee;
  }
  if (policyType === PolicyType.Auto) {
    return !!fees?.auto?.applyCreditCardFee;
  }
  return false;
}

/**
 * Card charges per term that an annualized credit-card fee line is spread over: a 6-month auto term
 * (autoPaymentCount) or a 12-month farm term (farmPaymentCount). The rater multiplies by it and billing
 * divides by it, so both must use this.
 */
export function creditCardFeePaymentCount({
  policyType,
  paymentType,
  installmentCount
}: {
  policyType: string;
  paymentType: string;
  /** The term's own installment count when it isn't the term length: a new-business auto Monthly term
   * on a brand's fixed plan (CFM's 5 Pay, #36492) — from the brand config at rating, from the policy's
   * `newBusinessInstallmentCount` stamp in billing. */
  installmentCount?: number;
}): number {
  if (policyType === PolicyType.Auto) {
    return paymentType === PaymentType.Monthly && installmentCount ? installmentCount : autoPaymentCount(paymentType);
  }
  return farmPaymentCount(paymentType);
}

/**
 * Builds the annualized credit-card processing fee line item for a farm or (CFM, #36200) auto policy. Intended to
 * be pushed into `farmFees` during rating, AFTER all other fees are computed so the base
 * installment amount is stable. The fee is annualized (per-charge × paymentCount) so that
 * summing `farmFees` yields the customer-facing annual total.
 *
 * Two variants:
 * - Regular CC (`paymentMethod === 'C'`): every installment is via CC, so the fee is annualized
 *   (per-charge × paymentCount) and distributed evenly across installments at charge time.
 * - Direct billing with CC first installment (`paymentMethod === 'D'` AND
 *   `directBillPreferredPaymentMethod === 'C'`): only installment #1 is via CC at bind; the cron
 *   sends links for 2..N where the customer picks a payment method per installment, so we
 *   genuinely have only one CC charge. The fee is therefore the per-charge amount (NOT × N), and
 *   it's marked `firstInstallmentOnly: true` so it lands entirely on installment #1.
 *
 * Returns `null` when the brand / policy type / payment method combination doesn't apply the
 * CC fee; caller should skip the push in that case.
 */
export function generateAnnualizedCreditCardFee({
  preCcFeeTotal,
  paymentType,
  paymentMethod,
  directBillPreferredPaymentMethod,
  brandConfig,
  policyType,
  appliedDate,
  installmentCount
}: {
  preCcFeeTotal: number;
  paymentType: string;
  paymentMethod: string;
  directBillPreferredPaymentMethod?: string;
  brandConfig?: any;
  policyType: string;
  appliedDate?: string;
  /** The charges the line is spread over when the term isn't on the term length: the offer rater passes
   * a brand's fixed new-business auto Monthly plan (CFM's 5 Pay, #36492). Absent = the term length, which
   * is what billing divides by for a policy with no plan stamp (an import, an earlier bind). */
  installmentCount?: number;
}): {
  type: string;
  description: string;
  amount: number;
  appliedDate?: string;
  firstInstallmentOnly?: boolean;
} | null {
  // Direct billing or BillToOther with a CC first-charge method: only one CC charge happens
  // (installment #1 for Direct, full annual at bind for OneTime/BillToOther). The cron handles
  // 2..N via direct-billing links where the customer picks payment method per-installment, so
  // we genuinely have only one CC charge to fee.
  const isFirstChargeCC =
    (paymentMethod === PaymentMethod.Direct || paymentMethod === PaymentMethod.BillToOther) &&
    directBillPreferredPaymentMethod === PaymentMethod.CreditCard;
  if (paymentMethod !== PaymentMethod.CreditCard && !isFirstChargeCC) {
    console.log('[fee-utils][cc-fee] skip: paymentMethod is not CC', {
      paymentMethod,
      directBillPreferredPaymentMethod
    });
    return null;
  }
  if (!appliesCreditCardFee({ brandConfig, policyType })) {
    console.log('[fee-utils][cc-fee] skip: brand does not apply CC fee to this policy type', { policyType });
    return null;
  }
  if (!preCcFeeTotal || preCcFeeTotal <= 0) {
    console.log('[fee-utils][cc-fee] skip: preCcFeeTotal invalid', { preCcFeeTotal });
    return null;
  }

  const paymentCount = creditCardFeePaymentCount({ policyType, paymentType, installmentCount });
  const baseInstallment = preCcFeeTotal / paymentCount;
  const perCharge = roundTwoDecimals(0.25 + baseInstallment * 0.0285);
  // Regular CC: every installment is via CC → total fee is annualized (per-charge × paymentCount).
  // First-charge-CC (Direct or BillToOther + CC first-charge method): only one CC charge → fee is
  // a single per-charge amount.
  const amount = isFirstChargeCC ? perCharge : roundTwoDecimals(perCharge * paymentCount);

  console.log('[fee-utils][cc-fee] computed', {
    preCcFeeTotal,
    paymentType,
    paymentCount,
    baseInstallment: roundTwoDecimals(baseInstallment),
    formula: `0.25 + ${roundTwoDecimals(baseInstallment)} × 0.0285 = ${perCharge}`,
    perCharge,
    amount,
    isFirstChargeCC,
    firstInstallmentOnly: isFirstChargeCC
  });

  return {
    type: FeeType.CreditCard,
    description: 'Credit Card Processing Fee',
    amount,
    appliedDate,
    ...(isFirstChargeCC ? { firstInstallmentOnly: true } : {})
  };
}

/**
 * The slice of a card-paid policy's annualized `credit_card` fee line that survives a cancellation
 * (issue #36221): the processing fee Branch actually collected on the member's settled card charges
 * (the sum of their Stripe Connect application fees, net of refunds — see
 * BillingPreviewManager.sumCreditCardFeesCollected). The rest of the line covered installments that
 * will never be charged, so it comes off the policy — on a flat cancel (which used to refund the
 * whole line, handing back a fee Branch had already paid Stripe) and on a mid-term cancel alike
 * (which used to keep the whole term's line). Tying it to the collected fee rather than a count of
 * charges keeps a manual or partial card charge from retaining a full installment's fee.
 *
 * The kept amount is capped at the baked line. Returns a new fee object flagged
 * `retainedOnCancellation` (the refund path reads that flag to keep Branch's application fee too),
 * or `null` when there is nothing to keep.
 */
export function retainCollectedCreditCardFee({
  fee,
  creditCardFeesCollected
}: {
  fee: { type?: string; amount?: number } & Record<string, unknown>;
  creditCardFeesCollected: number;
}): ({ type: string; amount: number; retainedOnCancellation: true } & Record<string, unknown>) | null {
  const annualAmount = Number(fee?.amount) || 0;
  const collected = roundTwoDecimals(Number(creditCardFeesCollected) || 0);
  if (annualAmount <= 0 || collected <= 0) {
    return null;
  }
  return {
    ...fee,
    type: String(fee.type ?? FeeType.CreditCard),
    amount: Math.min(annualAmount, collected),
    retainedOnCancellation: true
  };
}

/** Did a cancellation keep (part of) the credit-card fee from the member? See retainCreditCardFeeForPaymentsMade. */
export function isCreditCardFeeRetainedOnCancellation(
  fees: Array<{ type?: string; retainedOnCancellation?: boolean }> | null | undefined
): boolean {
  return (
    Array.isArray(fees) && fees.some((fee) => fee?.type === FeeType.CreditCard && fee?.retainedOnCancellation === true)
  );
}
